import styled from '@emotion/styled';
import { useEffect, useRef, useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { ToastManager } from '../../../ToastManager';
import * as Containers from '../Containers';

import { devicesStore } from '../../../store/devices/DevicesStore';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useMount } from '../../../hooks/useMount';
import { useShallowMemo } from '../../../hooks/useShallowMemo';
import { useDevice } from '../../../store/devices/useDevices';
import { useLogsForOwnerUri } from '../../../store/insights/useLogs';
import { useApi } from '../../../store/SelectedHomeyStore.hooks';

import { scrollbar } from '../../../theme/elements/scrollbars';
import { theme } from '../../../theme/theme';

import { TimelineItem } from './TimelineListItem';

export function Timeline(props) {
  const { i18n } = useI18n();
  const { device, deviceInstance } = useDevice({ deviceId: props.deviceId });

  const entries = useEntries({
    device: device,
    deviceInstance: deviceInstance,
  });

  return (
    <Containers.Control>
      <Containers.Select />
      <Containers.Action>
        <Timeline.Root>
          {entries?.map((entry, index) => {
            return (
              <TimelineItem
                key={index}
                entry={entry}
                messageFormatter={i18n.messageFormatter}
                dateFormatter={i18n.dateFormatter}
              />
            );
          })}

          {Boolean(entries?.length) === false && (
            <Timeline.EmptyMessage>
              {i18n.messageFormatter('device.noHistory')}
            </Timeline.EmptyMessage>
          )}
        </Timeline.Root>
      </Containers.Action>
    </Containers.Control>
  );
}

Timeline.Root = styled.ul`
  ${scrollbar};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  overflow-y: auto;
`;

Timeline.EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  color: ${theme.color.text_light};
`;

function useEntries({ device, deviceInstance }) {
  const mount = useMount();

  const instanceRef = useRef({
    pendingId: null,
  });

  const [state, setState] = useState(null);

  const { api } = useApi();
  const { logs } = useLogsForOwnerUri({ ownerUri: `homey:device:${device?.id}` });
  const logValues = useMemo(() => {
    if (logs == null) {
      return [];
    }

    return Object.values(logs);
  }, [logs]);
  // This ensures that refetching only happens when the logs of this device change. Not when the
  // logs collection object itself changes. Which can happen when logs.byId is updated.
  const memoizedLogs = useShallowMemo(() => {
    return logValues;
  }, logValues);

  useEffect(() => {
    if (deviceInstance == null || api == null) {
      return;
    }

    async function fetchEntries() {
      const id = (instanceRef.current.pendingId = uuid());

      try {
        const logsById = {};
        const promises = [];

        memoizedLogs.forEach((log) => {
          if (log.type !== 'boolean') {
            return;
          }

          logsById[log.id] = log;

          promises.push(
            // eslint-disable-next-line no-async-promise-executor
            new Promise(async (resolve, reject) => {
              try {
                const result = await api.insights.getLogEntries({
                  id: log.id,
                });
                resolve([log.id, result]);
              } catch (error) {
                reject(error);
              }
            }),
          );
        });

        const results = await Promise.all(promises);

        if (instanceRef.current.pendingId !== id) {
          return;
        }

        const values = [];

        results?.forEach(([id, result]) => {
          const log = logsById[id] ?? {};

          function getTitle(value) {
            return value ? log.titleTrue : log.titleFalse;
          }

          result.values?.forEach((entry, entryIndex) => {
            const date = new Date(entry.t);
            const value = entry.v === true;

            values.push({
              ...entry,
              date: date,
              title: getTitle(value),
              value: value,
              // key: `${date.getTime()}-${value.toString()}`,
              // expected date to be unique but it isnt somehow
              // key: entryIndex,
            });
          });
        });

        function dateSort(firstValue, secondValue) {
          return secondValue.date - firstValue.date;
        }

        if (mount.isMounted) {
          setState(values.sort(dateSort));
        }
      } catch (error) {
        if (instanceRef.current.pendingId !== id) return;
        ToastManager.handleError(error);
      }
    }

    function listener(event) {
      const capabilityObj = deviceInstance.capabilitiesObj?.[event.capabilityId];

      if (capabilityObj?.type !== 'boolean') {
        return;
      }

      new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
        fetchEntries();
      });
    }

    deviceInstance.on('capability', listener);

    fetchEntries();

    return function () {
      deviceInstance.off('capability', listener);
    };
  }, [mount, api, memoizedLogs, deviceInstance]);

  useEffect(() => {
    if (api == null || deviceInstance == null) {
      return;
    }

    const reference = instanceRef.current;

    devicesStore
      .connectItem({
        api,
        item: deviceInstance,
        reference,
      })
      .catch(ToastManager.handleError);

    return function () {
      devicesStore
        .disconnectItem({
          api,
          item: deviceInstance,
          reference,
        })
        .catch(ToastManager.handleError);
    };
  }, [api, deviceInstance]);

  return state;
}
